import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FundingApplicationService } from '@/services/funding-application.service';
import { useAuth } from '@/contexts/AuthContext';
import {
  FundingApplication,
  FundingApplicationStatus,
  FundingProgram,
  FundingProgramLabels,
  FundingApplicationStatusLabels,
  FundingTypeLabels,
} from '@/models/funding-application.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { DollarSign, ArrowLeft, Edit, CheckCircle2, XCircle, Clock, TrendingUp, Building2, Calendar, Mail, Phone, FileText } from 'lucide-react';

export const FundingApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();

  const [application, setApplication] = useState<FundingApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (id) {
      loadApplication();
    }
  }, [id]);

  const loadApplication = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const app = await FundingApplicationService.getById(id);
      setApplication(app);
    } catch (error) {
      console.error('Error loading funding application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: FundingApplicationStatus) => {
    if (!id || !profile?.id) return;

    setUpdating(true);
    try {
      const additionalData: any = {};

      if (newStatus === FundingApplicationStatus.SUBMITTED) {
        additionalData.submissionDate = new Date();
      }

      if (newStatus === FundingApplicationStatus.APPROVED) {
        const approvedAmount = prompt('Enter approved amount:');
        if (approvedAmount) {
          additionalData.approvedAmount = parseFloat(approvedAmount);
          additionalData.decisionDate = new Date();
        }
      }

      if (newStatus === FundingApplicationStatus.FUNDED) {
        const fundedAmount = prompt('Enter funded amount:');
        if (fundedAmount) {
          additionalData.fundedAmount = parseFloat(fundedAmount);
          additionalData.fundingDate = new Date();
        }
      }

      if (newStatus === FundingApplicationStatus.REJECTED) {
        additionalData.decisionDate = new Date();
        const lenderNotes = prompt('Enter rejection notes (optional):');
        if (lenderNotes) {
          additionalData.lenderNotes = lenderNotes;
        }
      }

      await FundingApplicationService.updateStatus(id, newStatus, profile.id, additionalData);
      await loadApplication();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const navigate = useNavigate();

  const getStatusVariant = (status: FundingApplicationStatus): "success" | "warning" | "danger" | "gray" | "info" => {
    switch (status) {
      case FundingApplicationStatus.DRAFT:
        return 'gray';
      case FundingApplicationStatus.SUBMITTED:
        return 'info';
      case FundingApplicationStatus.UNDER_REVIEW:
        return 'warning';
      case FundingApplicationStatus.APPROVED:
        return 'success';
      case FundingApplicationStatus.REJECTED:
        return 'danger';
      case FundingApplicationStatus.WITHDRAWN:
        return 'gray';
      case FundingApplicationStatus.FUNDED:
        return 'success';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading funding application..." fullScreen />;
  }

  if (!application) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={DollarSign}
          title="Funding application not found"
          description="The requested funding application does not exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/financial/funding')}>
              Back to Applications
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/financial/funding')}
          className="mb-4"
        >
          Back to Applications
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {application.program === FundingProgram.OTHER
                      ? application.programName
                      : FundingProgramLabels[application.program]}
                  </h1>
                  <Badge variant={getStatusVariant(application.status)} size="md">
                    {FundingApplicationStatusLabels[application.status]}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">Funding Application Details</p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/financial/funding/${application.id}/edit`)}
            >
              Edit
            </Button>
          </div>
        </Card>
      </div>

      {/* Status Actions */}
      <Card className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Status Actions</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {application.status === FundingApplicationStatus.DRAFT && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleStatusUpdate(FundingApplicationStatus.SUBMITTED)}
                isLoading={updating}
              >
                Mark as Submitted
              </Button>
            )}
            {application.status === FundingApplicationStatus.SUBMITTED && (
              <>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => handleStatusUpdate(FundingApplicationStatus.UNDER_REVIEW)}
                  isLoading={updating}
                >
                  Mark as Under Review
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  icon={CheckCircle2}
                  onClick={() => handleStatusUpdate(FundingApplicationStatus.APPROVED)}
                  isLoading={updating}
                >
                  Mark as Approved
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={XCircle}
                  onClick={() => handleStatusUpdate(FundingApplicationStatus.REJECTED)}
                  isLoading={updating}
                >
                  Mark as Rejected
                </Button>
              </>
            )}
            {application.status === FundingApplicationStatus.APPROVED && (
              <Button
                variant="success"
                size="sm"
                icon={TrendingUp}
                onClick={() => handleStatusUpdate(FundingApplicationStatus.FUNDED)}
                isLoading={updating}
              >
                Mark as Funded
              </Button>
            )}
          </div>
        </div>
      </Card>

        {/* Main Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Amount Information
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Requested Amount</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(application.requestedAmount)}
                </dd>
              </div>
              {application.approvedAmount && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Approved Amount</dt>
                  <dd className="mt-1 text-lg font-semibold text-green-600">
                    {formatCurrency(application.approvedAmount)}
                  </dd>
                </div>
              )}
              {application.fundedAmount && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Funded Amount</dt>
                  <dd className="mt-1 text-lg font-semibold text-emerald-600">
                    {formatCurrency(application.fundedAmount)}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Application Details
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Funding Type</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {FundingTypeLabels[application.fundingType]}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Application Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(application.applicationDate as Date)}
                </dd>
              </div>
              {application.submissionDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submission Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.submissionDate as Date)}
                  </dd>
                </div>
              )}
              {application.decisionDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Decision Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.decisionDate as Date)}
                  </dd>
                </div>
              )}
              {application.fundingDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Funding Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.fundingDate as Date)}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        </div>

        {/* Use of Funds */}
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Use of Funds
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{application.useOfFunds}</p>
        </Card>

        {/* Financial Details */}
        {(application.monthlyBurn || application.cloudAiSubscriptions || application.runwayMonths || application.earliestRevenueTrigger) && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Financial Details
            </h3>
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {application.monthlyBurn && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Monthly Burn</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">
                    {formatCurrency(application.monthlyBurn)}
                  </dd>
                </div>
              )}
              {application.runwayMonths && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Runway (Months)</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">
                    {application.runwayMonths}
                  </dd>
                </div>
              )}
              {application.earliestRevenueTrigger && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Earliest Revenue Trigger</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {application.earliestRevenueTrigger}
                  </dd>
                </div>
              )}
            </dl>
            {application.cloudAiSubscriptions && (
              <div className="mt-4 pt-4 border-t">
                <dt className="text-sm font-medium text-gray-500 mb-2">Cloud/AI Subscriptions Breakdown</dt>
                <dd className="text-sm text-gray-700 whitespace-pre-wrap">
                  {application.cloudAiSubscriptions}
                </dd>
              </div>
            )}
          </Card>
        )}

        {/* Business Information */}
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Business Information
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Operating State</dt>
              <dd className="mt-1 text-sm text-gray-900">{application.operatingState}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Business Type</dt>
              <dd className="mt-1 text-sm text-gray-900">{application.businessType}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Incorporation State</dt>
              <dd className="mt-1 text-sm text-gray-900">{application.incorporationState}</dd>
            </div>
            {application.foreignQualificationStatus && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Foreign Qualification Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {application.foreignQualificationStatus.replace('_', ' ').toUpperCase()}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Lender Contact */}
        {(application.lenderContactName ||
          application.lenderContactEmail ||
          application.lenderContactPhone ||
          application.applicationNumber) && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Lender Contact
            </h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {application.lenderContactName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contact Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{application.lenderContactName}</dd>
                </div>
              )}
              {application.lenderContactEmail && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Email
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      href={`mailto:${application.lenderContactEmail}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {application.lenderContactEmail}
                    </a>
                  </dd>
                </div>
              )}
              {application.lenderContactPhone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Phone
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      href={`tel:${application.lenderContactPhone}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {application.lenderContactPhone}
                    </a>
                  </dd>
                </div>
              )}
              {application.applicationNumber && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{application.applicationNumber}</dd>
                </div>
              )}
            </dl>
          </Card>
        )}

        {/* Dates */}
        {(application.expectedDecisionDate || application.nextFollowUpDate) && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Important Dates
            </h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {application.expectedDecisionDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expected Decision Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.expectedDecisionDate as Date)}
                  </dd>
                </div>
              )}
              {application.nextFollowUpDate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Next Follow-up Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.nextFollowUpDate as Date)}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        )}

        {/* Notes */}
        {application.notes && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Internal Notes
            </h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{application.notes}</p>
          </Card>
        )}

        {/* Lender Notes */}
        {application.lenderNotes && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Lender Notes
            </h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{application.lenderNotes}</p>
          </Card>
        )}
    </div>
  );
};

